import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import {
  Activity,
  ArrowLeft,
  Building2,
  Check,
  Loader2,
  ShieldCheck,
  UserPlus,
  Users,
} from 'lucide-react';
import { apiFetch } from '../lib/api';
import { useAuthStore } from '../store/authStore';

/**
 * GESTOR DE USUARIOS Y PLANES — solo superadmin.
 *
 * El modelo comercial (22-ago-2026), dos familias:
 * - PERSONALES (Voz, Diálogo, Tribuna): usuarios en el tenant de ÁGORA; se
 *   raciona su tiempo, herramientas y procesos. Van como override de usuario.
 * - DE ORGANIZACIÓN (Foro 1/3/5, Ágora Plena): tenant propio con identidad;
 *   se cobra por asientos de facilitador y los consumos son pozo mensual de
 *   la org. Van en la org.
 * Crear un cliente nuevo = org con plan + su primer facilitador en un solo
 * alta; sin contraseña, la persona entra con Google (pre-vinculación por
 * correo, mismo mecanismo del bootstrap).
 */

interface PlanAdmin {
  codigo: string;
  nombre: string;
  orden: number;
  limites: Record<string, unknown>;
  activo: boolean;
}

interface OrgAdmin {
  id: string;
  nombre: string;
  slug: string;
  plan_codigo: string | null;
  plan_nombre: string | null;
  usuarios: number;
  procesos_mes: number;
  grabacion_min_mes: number;
}

interface KpisAdmin {
  desde: string;
  procesos_creados_mes: number;
  aportes_mes: number;
  minutos_audio_mes: number;
  usuarios_activos_mes: number;
  tenants: {
    slug: string;
    nombre: string;
    plan: string | null;
    procesos_mes: number;
    procesos_total: number;
    aportes_mes: number;
    minutos_audio_mes: number;
    usuarios_activos_mes: number;
    usuarios_total: number;
  }[];
  top_procesos: {
    nombre: string;
    org_slug: string;
    aportes_mes: number;
    minutos_audio_mes: number;
    personas_mes: number;
  }[];
}

interface UsuarioAdmin {
  id: string;
  nombre: string;
  email: string | null;
  rol_global: string;
  activo: boolean;
  org_id: string;
  org_nombre: string;
  org_slug: string;
  plan_codigo: string | null;
  plan_efectivo: string;
  tiene_password: boolean;
}

const ROLES = ['miembro', 'facilitador', 'superadmin'] as const;

/** Dos familias: personales (override de usuario) y de organización (tenant). */
const esPersonal = (p: PlanAdmin) => p.limites.ambito === 'personal';
const esOrg = (p: PlanAdmin) => p.limites.ambito !== 'personal';

function limiteHumano(v: unknown): string {
  if (v === null || v === undefined) return 'sin tope';
  if (v === true) return 'sí';
  if (v === false) return '—';
  if (Array.isArray(v)) return v.join(', ');
  return String(v);
}

export function AdminPage() {
  const user = useAuthStore((s) => s.user);
  const qc = useQueryClient();
  const esSuper = user?.rol === 'superadmin';

  const planes = useQuery({
    queryKey: ['admin', 'planes'],
    queryFn: () => apiFetch<PlanAdmin[]>('/admin/planes'),
    enabled: esSuper,
  });
  const orgs = useQuery({
    queryKey: ['admin', 'orgs'],
    queryFn: () => apiFetch<OrgAdmin[]>('/admin/orgs'),
    enabled: esSuper,
  });
  const usuarios = useQuery({
    queryKey: ['admin', 'usuarios'],
    queryFn: () => apiFetch<UsuarioAdmin[]>('/admin/usuarios'),
    enabled: esSuper,
  });
  const kpis = useQuery({
    queryKey: ['admin', 'kpis'],
    queryFn: () => apiFetch<KpisAdmin>('/admin/kpis'),
    enabled: esSuper,
    refetchInterval: 60_000,
  });

  const invalidar = () => {
    void qc.invalidateQueries({ queryKey: ['admin'] });
  };

  const patchUsuario = useMutation({
    mutationFn: (v: { id: string; cambios: Record<string, unknown> }) =>
      apiFetch<UsuarioAdmin>(`/admin/usuarios/${v.id}`, { method: 'PATCH', json: v.cambios }),
    onSuccess: invalidar,
  });
  const patchOrg = useMutation({
    mutationFn: (v: { id: string; plan_codigo: string }) =>
      apiFetch<OrgAdmin>(`/admin/orgs/${v.id}`, {
        method: 'PATCH',
        json: { plan_codigo: v.plan_codigo },
      }),
    onSuccess: invalidar,
  });

  if (!esSuper) {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center gap-3 bg-surface-base px-6 text-center">
        <ShieldCheck className="h-10 w-10 text-content-muted" aria-hidden="true" />
        <p className="font-sans text-base text-content">
          El gestor de usuarios es solo para el superadmin de la plataforma.
        </p>
        <Link to="/cabina" className="font-sans text-sm text-navy underline">
          Volver a la Cabina
        </Link>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-surface-base pb-16">
      <header className="border-b border-border bg-surface-raised px-4 py-3 sm:px-6">
        <div className="flex items-center gap-3">
          <Link
            to="/cabina"
            className="flex min-h-touch items-center gap-1.5 rounded-md font-sans text-sm text-content-muted hover:text-content"
          >
            <ArrowLeft className="h-4 w-4" aria-hidden="true" />
            Cabina
          </Link>
          <h1 className="font-display text-lg font-bold text-content">
            Usuarios y planes
          </h1>
        </div>
        <p className="mt-1 font-sans text-xs text-content-muted">
          Org = tenant = cliente: el plan se vende a la organización y un usuario
          puede llevar un override individual. Sin contraseña, la persona entra
          con Google (queda pre-vinculada por su correo).
        </p>
      </header>

      <main className="mx-auto flex max-w-6xl flex-col gap-8 px-4 py-6 sm:px-6">
        {/* ── KPIs del mes: todo atribuido a la org DUEÑA del proceso ── */}
        {kpis.data && (
          <section>
            <h2 className="flex items-center gap-2 font-display text-base font-semibold text-content">
              <Activity className="h-4 w-4" aria-hidden="true" />
              Consumo del mes
              <span className="font-sans text-xs font-normal text-content-muted">
                desde el {kpis.data.desde} — atribuido al tenant dueño de cada proceso
              </span>
            </h2>
            <div className="mt-2 grid grid-cols-2 gap-3 lg:grid-cols-4">
              {[
                { n: kpis.data.procesos_creados_mes, l: 'procesos creados' },
                { n: kpis.data.aportes_mes, l: 'aportes recibidos' },
                {
                  n: `${Math.floor(kpis.data.minutos_audio_mes / 60)} h ${kpis.data.minutos_audio_mes % 60} m`,
                  l: 'de audio grabado',
                },
                { n: kpis.data.usuarios_activos_mes, l: 'personas aportando' },
              ].map((k) => (
                <div
                  key={k.l}
                  className="rounded-xl border border-border bg-surface-raised px-4 py-3"
                >
                  <p className="font-display text-2xl font-bold text-content">{k.n}</p>
                  <p className="font-sans text-xs text-content-muted">{k.l}</p>
                </div>
              ))}
            </div>
            <div className="mt-3 grid gap-3 lg:grid-cols-2">
              <div className="overflow-x-auto rounded-xl border border-border">
                <table className="w-full min-w-[420px] font-sans text-sm">
                  <thead className="bg-surface-raised text-left text-xs uppercase tracking-wide text-content-muted">
                    <tr>
                      <th className="px-3 py-2">Tenant</th>
                      <th className="px-3 py-2">Aportes</th>
                      <th className="px-3 py-2">Audio</th>
                      <th className="px-3 py-2">Personas</th>
                      <th className="px-3 py-2">Procesos (mes/total)</th>
                    </tr>
                  </thead>
                  <tbody>
                    {kpis.data.tenants.map((tn) => (
                      <tr key={tn.slug} className="border-t border-border">
                        <td className="px-3 py-2">
                          <span className="font-medium text-content">{tn.nombre}</span>
                          {tn.plan && (
                            <span className="ml-2 text-xs text-content-muted">{tn.plan}</span>
                          )}
                        </td>
                        <td className="px-3 py-2 text-content">{tn.aportes_mes}</td>
                        <td className="px-3 py-2 text-content">{tn.minutos_audio_mes} min</td>
                        <td className="px-3 py-2 text-content">{tn.usuarios_activos_mes}</td>
                        <td className="px-3 py-2 text-content">
                          {tn.procesos_mes}/{tn.procesos_total}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <div className="overflow-x-auto rounded-xl border border-border">
                <table className="w-full min-w-[420px] font-sans text-sm">
                  <thead className="bg-surface-raised text-left text-xs uppercase tracking-wide text-content-muted">
                    <tr>
                      <th className="px-3 py-2">Proceso más activo</th>
                      <th className="px-3 py-2">Tenant</th>
                      <th className="px-3 py-2">Aportes</th>
                      <th className="px-3 py-2">Audio</th>
                      <th className="px-3 py-2">Personas</th>
                    </tr>
                  </thead>
                  <tbody>
                    {kpis.data.top_procesos.map((pr) => (
                      <tr key={pr.nombre + pr.org_slug} className="border-t border-border">
                        <td className="max-w-[220px] truncate px-3 py-2 font-medium text-content">
                          {pr.nombre}
                        </td>
                        <td className="px-3 py-2 text-content-muted">{pr.org_slug}</td>
                        <td className="px-3 py-2 text-content">{pr.aportes_mes}</td>
                        <td className="px-3 py-2 text-content">{pr.minutos_audio_mes} min</td>
                        <td className="px-3 py-2 text-content">{pr.personas_mes}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </section>
        )}

        {/* ── Categorías: dos familias ── */}
        <section>
          <h2 className="font-display text-base font-semibold text-content">
            Planes personales
            <span className="ml-2 font-sans text-xs font-normal text-content-muted">
              para usuarios en el tenant de ÁGORA — se raciona su tiempo, herramientas y procesos
            </span>
          </h2>
          <div className="mt-2 grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
            {(planes.data ?? []).filter(esPersonal).map((p) => (
              <article
                key={p.codigo}
                className="rounded-xl border border-border bg-surface-raised p-3"
              >
                <p className="font-display text-sm font-bold text-content">{p.nombre}</p>
                <dl className="mt-2 space-y-0.5 font-sans text-[11px] text-content-muted">
                  <div className="flex justify-between gap-2">
                    <dt>Procesos/mes</dt>
                    <dd className="text-content">{limiteHumano(p.limites.procesos_mes)}</dd>
                  </div>
                  <div className="flex justify-between gap-2">
                    <dt>Grabación</dt>
                    <dd className="text-content">
                      {p.limites.grabacion_min_sesion != null
                        ? `${Number(p.limites.grabacion_min_sesion) / 60} h/sesión`
                        : p.limites.grabacion_min_mes != null
                          ? `${Math.round(Number(p.limites.grabacion_min_mes) / 60)} h/mes`
                          : 'sin tope'}
                    </dd>
                  </div>
                  <div className="flex justify-between gap-2">
                    <dt>Herramientas</dt>
                    <dd className="text-right text-content">
                      {p.limites.familias ? limiteHumano(p.limites.familias) : 'todas'}
                    </dd>
                  </div>
                  <div className="flex justify-between gap-2">
                    <dt>Participantes/sesión</dt>
                    <dd className="text-content">{limiteHumano(p.limites.participantes_sesion)}</dd>
                  </div>
                  <div className="flex justify-between gap-2">
                    <dt>Mic. de sala</dt>
                    <dd className="text-content">{limiteHumano(p.limites.microfono_sala)}</dd>
                  </div>
                  <div className="flex justify-between gap-2">
                    <dt>Validación docs</dt>
                    <dd className="text-content">
                      {limiteHumano(p.limites.validacion_documentos)}
                    </dd>
                  </div>
                </dl>
              </article>
            ))}
          </div>
          <h2 className="mt-5 font-display text-base font-semibold text-content">
            Planes de organización
            <span className="ml-2 font-sans text-xs font-normal text-content-muted">
              tenant propio con su identidad — asientos de facilitador y pozo mensual de la org
            </span>
          </h2>
          <div className="mt-2 grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
            {(planes.data ?? []).filter(esOrg).map((p) => (
              <article
                key={p.codigo}
                className="rounded-xl border border-border bg-surface-raised p-3"
              >
                <p className="font-display text-sm font-bold text-content">{p.nombre}</p>
                <dl className="mt-2 space-y-0.5 font-sans text-[11px] text-content-muted">
                  <div className="flex justify-between gap-2">
                    <dt>Facilitadores</dt>
                    <dd className="text-content">
                      {p.limites.facilitadores == null
                        ? 'ilimitados (contactar)'
                        : String(p.limites.facilitadores)}
                    </dd>
                  </div>
                  <div className="flex justify-between gap-2">
                    <dt>Procesos/mes (org)</dt>
                    <dd className="text-content">{limiteHumano(p.limites.procesos_mes)}</dd>
                  </div>
                  <div className="flex justify-between gap-2">
                    <dt>Grabación/mes (org)</dt>
                    <dd className="text-content">
                      {p.limites.grabacion_min_mes == null
                        ? 'sin tope'
                        : `${Math.round(Number(p.limites.grabacion_min_mes) / 60)} h`}
                    </dd>
                  </div>
                  <div className="flex justify-between gap-2">
                    <dt>Participantes/sesión</dt>
                    <dd className="text-content">{limiteHumano(p.limites.participantes_sesion)}</dd>
                  </div>
                  <div className="flex justify-between gap-2">
                    <dt>Producto 2</dt>
                    <dd className="text-content">{limiteHumano(p.limites.producto2)}</dd>
                  </div>
                </dl>
              </article>
            ))}
          </div>
        </section>

        {/* ── Tenants ── */}
        <section>
          <h2 className="flex items-center gap-2 font-display text-base font-semibold text-content">
            <Building2 className="h-4 w-4" aria-hidden="true" />
            Tenants (organizaciones)
          </h2>
          <div className="mt-2 overflow-x-auto rounded-xl border border-border">
            <table className="w-full min-w-[640px] font-sans text-sm">
              <thead className="bg-surface-raised text-left text-xs uppercase tracking-wide text-content-muted">
                <tr>
                  <th className="px-3 py-2">Organización</th>
                  <th className="px-3 py-2">Plan</th>
                  <th className="px-3 py-2">Usuarios</th>
                  <th className="px-3 py-2">Procesos este mes</th>
                  <th className="px-3 py-2">Grabación este mes</th>
                </tr>
              </thead>
              <tbody>
                {(orgs.data ?? []).map((o) => (
                  <tr key={o.id} className="border-t border-border">
                    <td className="px-3 py-2">
                      <span className="font-medium text-content">{o.nombre}</span>
                      <span className="ml-2 text-xs text-content-muted">{o.slug}</span>
                    </td>
                    <td className="px-3 py-2">
                      <select
                        value={o.plan_codigo ?? ''}
                        onChange={(e) =>
                          e.target.value &&
                          patchOrg.mutate({ id: o.id, plan_codigo: e.target.value })
                        }
                        className="rounded border border-border-strong bg-surface-raised px-2 py-1 text-sm text-content"
                        aria-label={`Plan de ${o.nombre}`}
                      >
                        {!o.plan_codigo && <option value="">Voz (por defecto)</option>}
                        {(planes.data ?? []).filter(esOrg).map((p) => (
                          <option key={p.codigo} value={p.codigo}>
                            {p.nombre}
                          </option>
                        ))}
                      </select>
                    </td>
                    <td className="px-3 py-2 text-content">{o.usuarios}</td>
                    <td className="px-3 py-2 text-content">{o.procesos_mes}</td>
                    <td className="px-3 py-2 text-content">{o.grabacion_min_mes} min</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>

        {/* ── Alta ── */}
        <AltaUsuario
          orgs={orgs.data ?? []}
          planes={planes.data ?? []}
          onCreado={invalidar}
        />

        {/* ── Usuarios ── */}
        <section>
          <h2 className="flex items-center gap-2 font-display text-base font-semibold text-content">
            <Users className="h-4 w-4" aria-hidden="true" />
            Usuarios ({(usuarios.data ?? []).length})
          </h2>
          <div className="mt-2 overflow-x-auto rounded-xl border border-border">
            <table className="w-full min-w-[760px] font-sans text-sm">
              <thead className="bg-surface-raised text-left text-xs uppercase tracking-wide text-content-muted">
                <tr>
                  <th className="px-3 py-2">Persona</th>
                  <th className="px-3 py-2">Organización</th>
                  <th className="px-3 py-2">Rol</th>
                  <th className="px-3 py-2">Plan (override)</th>
                  <th className="px-3 py-2">Rige</th>
                  <th className="px-3 py-2">Entrada</th>
                  <th className="px-3 py-2">Activo</th>
                </tr>
              </thead>
              <tbody>
                {(usuarios.data ?? []).map((u) => (
                  <tr key={u.id} className={u.activo ? 'border-t border-border' : 'border-t border-border opacity-50'}>
                    <td className="px-3 py-2">
                      <span className="font-medium text-content">{u.nombre}</span>
                      {u.email && (
                        <span className="ml-2 text-xs text-content-muted">{u.email}</span>
                      )}
                    </td>
                    <td className="px-3 py-2 text-content-muted">{u.org_nombre}</td>
                    <td className="px-3 py-2">
                      <select
                        value={u.rol_global}
                        onChange={(e) =>
                          patchUsuario.mutate({
                            id: u.id,
                            cambios: { rol_global: e.target.value },
                          })
                        }
                        className="rounded border border-border-strong bg-surface-raised px-2 py-1 text-sm text-content"
                        aria-label={`Rol de ${u.nombre}`}
                      >
                        {ROLES.map((r) => (
                          <option key={r} value={r}>
                            {r}
                          </option>
                        ))}
                      </select>
                    </td>
                    <td className="px-3 py-2">
                      <select
                        value={u.plan_codigo ?? ''}
                        onChange={(e) =>
                          patchUsuario.mutate({
                            id: u.id,
                            cambios: e.target.value
                              ? { plan_codigo: e.target.value }
                              : { quitar_plan: true },
                          })
                        }
                        className="rounded border border-border-strong bg-surface-raised px-2 py-1 text-sm text-content"
                        aria-label={`Plan individual de ${u.nombre}`}
                      >
                        <option value="">— hereda org —</option>
                        {(planes.data ?? []).filter(esPersonal).map((p) => (
                          <option key={p.codigo} value={p.codigo}>
                            {p.nombre}
                          </option>
                        ))}
                      </select>
                    </td>
                    <td className="px-3 py-2 text-content">{u.plan_efectivo}</td>
                    <td className="px-3 py-2 text-xs text-content-muted">
                      {u.tiene_password ? 'contraseña + Google' : 'Google'}
                    </td>
                    <td className="px-3 py-2">
                      <input
                        type="checkbox"
                        checked={u.activo}
                        onChange={(e) =>
                          patchUsuario.mutate({
                            id: u.id,
                            cambios: { activo: e.target.checked },
                          })
                        }
                        className="h-4 w-4"
                        aria-label={`${u.nombre} activo`}
                      />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {patchUsuario.isError && (
            <p className="mt-2 font-sans text-xs text-error">
              {(patchUsuario.error as { detail?: string })?.detail ??
                'No se pudo aplicar el cambio.'}
            </p>
          )}
        </section>
      </main>
    </div>
  );
}

function AltaUsuario({
  orgs,
  planes,
  onCreado,
}: {
  orgs: OrgAdmin[];
  planes: PlanAdmin[];
  onCreado: () => void;
}) {
  const [nombre, setNombre] = useState('');
  const [email, setEmail] = useState('');
  const [rol, setRol] = useState<string>('facilitador');
  const [password, setPassword] = useState('');
  const [modoOrg, setModoOrg] = useState<'existente' | 'nueva'>('nueva');
  const [orgId, setOrgId] = useState('');
  const [orgNombre, setOrgNombre] = useState('');
  const [orgPlan, setOrgPlan] = useState('voz');
  const [creado, setCreado] = useState<string | null>(null);

  const crear = useMutation({
    mutationFn: () =>
      apiFetch<UsuarioAdmin>('/admin/usuarios', {
        method: 'POST',
        json: {
          nombre: nombre.trim(),
          email: email.trim(),
          rol_global: rol,
          password: password || null,
          ...(modoOrg === 'nueva'
            ? { org_nueva: { nombre: orgNombre.trim(), plan_codigo: orgPlan } }
            : { org_id: orgId }),
        },
      }),
    onSuccess: (u) => {
      setCreado(
        `${u.nombre} creado en «${u.org_nombre}» (plan ${u.plan_efectivo}). ` +
          (u.tiene_password
            ? 'Entra con su contraseña o con Google.'
            : 'Entra con Google usando su correo.'),
      );
      setNombre('');
      setEmail('');
      setPassword('');
      setOrgNombre('');
      onCreado();
    },
  });

  const valido =
    nombre.trim().length >= 2 &&
    /\S+@\S+\.\S+/.test(email) &&
    (password === '' || password.length >= 8) &&
    (modoOrg === 'nueva' ? orgNombre.trim().length >= 2 : orgId !== '');

  return (
    <section className="rounded-xl border border-border bg-surface-raised p-4">
      <h2 className="flex items-center gap-2 font-display text-base font-semibold text-content">
        <UserPlus className="h-4 w-4" aria-hidden="true" />
        Crear usuario
      </h2>
      <form
        className="mt-3 grid gap-3 sm:grid-cols-2 lg:grid-cols-3"
        onSubmit={(e) => {
          e.preventDefault();
          if (valido) crear.mutate();
        }}
      >
        <label className="flex flex-col gap-1 font-sans text-xs text-content-muted">
          Nombre
          <input
            value={nombre}
            onChange={(e) => setNombre(e.target.value)}
            className="rounded-lg border border-border bg-surface-base px-3 py-2 font-sans text-sm text-content"
            placeholder="Ana Morales"
          />
        </label>
        <label className="flex flex-col gap-1 font-sans text-xs text-content-muted">
          Correo (con este entra con Google)
          <input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className="rounded-lg border border-border bg-surface-base px-3 py-2 font-sans text-sm text-content"
            placeholder="ana@organizacion.gt"
          />
        </label>
        <label className="flex flex-col gap-1 font-sans text-xs text-content-muted">
          Contraseña (opcional: vacía = solo Google)
          <input
            type="text"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            className="rounded-lg border border-border bg-surface-base px-3 py-2 font-sans text-sm text-content"
            placeholder="mínimo 8 caracteres"
          />
        </label>
        <label className="flex flex-col gap-1 font-sans text-xs text-content-muted">
          Rol
          <select
            value={rol}
            onChange={(e) => setRol(e.target.value)}
            className="rounded-lg border border-border bg-surface-base px-3 py-2 font-sans text-sm text-content"
          >
            {ROLES.map((r) => (
              <option key={r} value={r}>
                {r}
              </option>
            ))}
          </select>
        </label>
        <label className="flex flex-col gap-1 font-sans text-xs text-content-muted">
          Cliente
          <select
            value={modoOrg === 'nueva' ? '__nueva__' : orgId}
            onChange={(e) => {
              if (e.target.value === '__nueva__') setModoOrg('nueva');
              else {
                setModoOrg('existente');
                setOrgId(e.target.value);
              }
            }}
            className="rounded-lg border border-border bg-surface-base px-3 py-2 font-sans text-sm text-content"
          >
            <option value="__nueva__">+ Cliente nuevo (org nueva)</option>
            {orgs.map((o) => (
              <option key={o.id} value={o.id}>
                {o.nombre}
              </option>
            ))}
          </select>
        </label>
        {modoOrg === 'nueva' && (
          <>
            <label className="flex flex-col gap-1 font-sans text-xs text-content-muted">
              Nombre de la organización nueva
              <input
                value={orgNombre}
                onChange={(e) => setOrgNombre(e.target.value)}
                className="rounded-lg border border-border bg-surface-base px-3 py-2 font-sans text-sm text-content"
                placeholder="Municipalidad de Antigua"
              />
            </label>
            <label className="flex flex-col gap-1 font-sans text-xs text-content-muted">
              Plan del cliente
              <select
                value={orgPlan}
                onChange={(e) => setOrgPlan(e.target.value)}
                className="rounded-lg border border-border bg-surface-base px-3 py-2 font-sans text-sm text-content"
              >
                {planes.filter(esOrg).map((p) => (
                  <option key={p.codigo} value={p.codigo}>
                    {p.nombre}
                  </option>
                ))}
              </select>
            </label>
          </>
        )}
        <div className="flex items-end">
          <button
            type="submit"
            disabled={!valido || crear.isPending}
            className="inline-flex min-h-[40px] items-center gap-1.5 rounded-lg bg-navy px-4 py-2 font-sans text-sm font-semibold text-white transition hover:bg-navy/90 disabled:opacity-40"
          >
            {crear.isPending ? (
              <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
            ) : (
              <UserPlus className="h-4 w-4" aria-hidden="true" />
            )}
            Crear
          </button>
        </div>
      </form>
      {creado && (
        <p className="mt-2 flex items-center gap-1.5 font-sans text-xs text-success">
          <Check className="h-3.5 w-3.5" aria-hidden="true" />
          {creado}
        </p>
      )}
      {crear.isError && (
        <p className="mt-2 font-sans text-xs text-error">
          {(crear.error as { detail?: string })?.detail ?? 'No se pudo crear.'}
        </p>
      )}
    </section>
  );
}
